// src/App.js
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import api from "./api";

import POS from "./pages/POS";
import Login from "./pages/Login";
import Home from "./pages/Home";
import UserManager from "./pages/UserManager";
import RolesManager from "./pages/RolesManager";
import ProductManager from "./pages/ProductManager";
import PedidosManager from "./pages/PedidosManager";
import DashboardPedidos from "./pages/DashboardPedidos";
import CierresCaja from "./pages/CierresCaja";
import MovimientosCaja from "./pages/MovimientosCaja";
import ArqueosCaja from "./pages/ArqueosCaja";
import InformesCaja from "./pages/InformesCaja";
import Insumos from "./pages/Insumos";
import Inventario from "./pages/Inventario";

import Sidebar from "./components/Sidebar";
import ConfirmModal from "./components/ConfirmModal";
import Header from "./components/Header";
import UpdateModal from "./components/UpdateModal";
import ErrorBoundary from "./components/ErrorBoundary";
import FloatingDashboardPedidos from "./components/FloatingDashboardPedidos";

import "./components/Sidebar.css";
import "./components/Header.css";

function PermissionRoute({ isAuth, user, permissionNeeded, children, authReady = true }) {
  if (!authReady) return null;
  if (!isAuth) return <Navigate to="/login" replace />;
  const required = Array.isArray(permissionNeeded) ? permissionNeeded : [permissionNeeded];
  const permisosDefinidos = Array.isArray(user?.permisos);
  const permisos = permisosDefinidos ? user.permisos : [];
  const fallbackAll = user?.rol === "superadmin";
  const fallbackPos = !permisosDefinidos && user?.rol === "vendedor" && required.includes("pos");
  const tiene =
    required.some((permiso) => permisos.includes(permiso)) || fallbackAll || fallbackPos;
  if (!user || !tiene) return <Navigate to="/pos" replace />;
  return children;
}

export default function App() {
  const [usuario, setUsuario] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [mostrarModalCerrarSesion, setMostrarModalCerrarSesion] = useState(false);

  // 👉 NUEVO: flag para mostrar el modal de actualización
  const [hayUpdateDisponible, setHayUpdateDisponible] = useState(false);

  // Estado de caja (compartido entre Header y POS para que el aviso desaparezca al iniciar)
  const [cajaAbierta, setCajaAbierta] = useState(null);

  useEffect(() => {
    const cargarUsuarioLocal = () => {
      try {
        const raw = localStorage.getItem("usuario");
        setUsuario(raw ? JSON.parse(raw) : null);
      } catch {
        setUsuario(null);
      }
    };

    try {
      const raw = localStorage.getItem("usuario");
      setUsuario(raw ? JSON.parse(raw) : null);
    } catch {
      setUsuario(null);
    } finally {
      setAuthReady(true);
    }

    const onStorage = (e) => {
      if (e.key === "usuario") {
        try {
          setUsuario(e.newValue ? JSON.parse(e.newValue) : null);
        } catch {
          setUsuario(null);
        }
      }
    };
    const onUsuarioActualizado = (e) => {
      if (e?.detail) {
        setUsuario(e.detail);
        return;
      }
      cargarUsuarioLocal();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("usuario-actualizado", onUsuarioActualizado);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("usuario-actualizado", onUsuarioActualizado);
    };
  }, []);

  // Consultar estado de caja al cargar usuario (para Header y POS)
  useEffect(() => {
    if (!usuario?.id) {
      setCajaAbierta(null);
      return;
    }
    let cancelled = false;
    api.get("/api/caja/estado", { params: { usuario_id: usuario.id } })
      .then(({ data }) => {
        if (!cancelled && data && typeof data.caja_abierta === "boolean") {
          setCajaAbierta(data.caja_abierta);
        }
      })
      .catch(() => {
        if (!cancelled) setCajaAbierta(false);
      });
    return () => { cancelled = true; };
  }, [usuario?.id]);

  const cerrarSesion = useCallback(() => {
    setMostrarModalCerrarSesion(true);
  }, []);

  const confirmarCerrarSesion = useCallback(() => {
    localStorage.removeItem("usuario");
    localStorage.removeItem("token");
    setUsuario(null);
    setMostrarModalCerrarSesion(false);
  }, []);

  // 👉 NUEVO: escuchar el evento global que dispara index.js cuando hay nueva versión
  useEffect(() => {
    const handler = () => {
      setHayUpdateDisponible(true);
    };

    window.addEventListener("app-update-available", handler);
    return () => window.removeEventListener("app-update-available", handler);
  }, []);

  // 👉 NUEVO: lógica del botón "Actualizar ahora"
  const manejarActualizarAhora = useCallback(async () => {
    try {
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg && reg.waiting) {
          // le pedimos al SW que haga skipWaiting
          reg.waiting.postMessage({ type: "SKIP_WAITING" });

          // cuando el SW nuevo se active, recargamos
          reg.waiting.addEventListener("statechange", (event) => {
            if (event.target.state === "activated") {
              window.location.reload();
            }
          });

          // por si acaso, fallback: recargar después de un tiempo
          setTimeout(() => {
            window.location.reload();
          }, 3000);
          return;
        }
      }

      // si no hay SW o algo falla, recargamos igual
      window.location.reload();
    } catch (e) {
      console.error("Error al actualizar la app:", e);
      window.location.reload();
    }
  }, []);

  return (
    <Router>
      <AppShell
        usuario={usuario}
        authReady={authReady}
        setUsuario={setUsuario}
        cerrarSesion={cerrarSesion}
        cajaAbierta={cajaAbierta}
        setCajaAbierta={setCajaAbierta}
        mostrarModalCerrarSesion={mostrarModalCerrarSesion}
        setMostrarModalCerrarSesion={setMostrarModalCerrarSesion}
        confirmarCerrarSesion={confirmarCerrarSesion}
        hayUpdateDisponible={hayUpdateDisponible}
        manejarActualizarAhora={manejarActualizarAhora}
      />
    </Router>
  );
}

/* ── Shell interno (necesita estar dentro de <Router> para usar useLocation) ── */
function AppShell({
  usuario, authReady, setUsuario, cerrarSesion, cajaAbierta, setCajaAbierta,
  mostrarModalCerrarSesion, setMostrarModalCerrarSesion, confirmarCerrarSesion,
  hayUpdateDisponible, manejarActualizarAhora,
}) {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [horariosAbierto, setHorariosAbierto] = useState(false);

  return (
    <>
      <div style={{ display: "flex", minHeight: "100vh" }}>
        {usuario && (
          <Sidebar usuario={usuario} cerrarSesion={cerrarSesion} />
        )}

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Header
            usuario={usuario}
            cerrarSesion={cerrarSesion}
            cajaAbierta={cajaAbierta}
            setCajaAbierta={setCajaAbierta}
            horariosAbierto={horariosAbierto}
            onOpenHorarios={() => setHorariosAbierto(true)}
          />

          <main
            className={`main-content${isHome ? " main-content--home" : ""}`}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              padding: isHome ? "0" : "20px",
            }}
          >
            <Routes>
              <Route
                path="/"
                element={<Home usuario={usuario} />}
              />

              <Route
                path="/login"
                element={
                  !usuario ? (
                    authReady ? <Login setUsuario={setUsuario} /> : null
                  ) : (
                    <Navigate to="/pos" replace />
                  )
                }
              />

              <Route
                path="/pos"
                element={
                  <ErrorBoundary>
                    <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="pos" authReady={authReady}>
                      <POS usuario={usuario} cerrarSesion={cerrarSesion} cajaAbierta={cajaAbierta} />
                    </PermissionRoute>
                  </ErrorBoundary>
                }
              />

              <Route
                path="/pedidos"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="pedidos" authReady={authReady}>
                    <PedidosManager usuario={usuario} />
                  </PermissionRoute>
                }
              />

              <Route
                path="/dashboard-pedidos"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="dashboard_pedidos" authReady={authReady}>
                    <DashboardPedidos />
                  </PermissionRoute>
                }
              />

              <Route
                path="/cierres"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="cierres" authReady={authReady}>
                    <CierresCaja usuario={usuario} />
                  </PermissionRoute>
                }
              />

              <Route
                path="/movimientos"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="movimientos" authReady={authReady}>
                    <MovimientosCaja usuario={usuario} cajaAbierta={cajaAbierta} />
                  </PermissionRoute>
                }
              />

              <Route
                path="/arqueos"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="arqueos" authReady={authReady}>
                    <ArqueosCaja usuario={usuario} />
                  </PermissionRoute>
                }
              />

              <Route
                path="/informes"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="informes" authReady={authReady}>
                    <InformesCaja usuario={usuario} />
                  </PermissionRoute>
                }
              />

              <Route
                path="/usuarios"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="usuarios" authReady={authReady}>
                    <UserManager />
                  </PermissionRoute>
                }
              />

              <Route
                path="/roles"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="roles" authReady={authReady}>
                    <RolesManager />
                  </PermissionRoute>
                }
              />

              <Route
                path="/productos"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="productos" authReady={authReady}>
                    <ProductManager />
                  </PermissionRoute>
                }
              />

              <Route
                path="/insumos"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded="insumos" authReady={authReady}>
                    <Insumos />
                  </PermissionRoute>
                }
              />

              <Route
                path="/inventario"
                element={
                  <PermissionRoute isAuth={!!usuario} user={usuario} permissionNeeded={["inventario", "insumos"]} authReady={authReady}>
                    <Inventario />
                  </PermissionRoute>
                }
              />

              <Route
                path="*"
                element={
                  authReady ? <Navigate to={usuario ? "/pos" : "/login"} replace /> : null
                }
              />
            </Routes>
          </main>
        </div>
      </div>

      {mostrarModalCerrarSesion && (
        <ConfirmModal
          mensaje="¿Estás segura de que quieres cerrar sesión?"
          onConfirmar={confirmarCerrarSesion}
          onCancelar={() => setMostrarModalCerrarSesion(false)}
        />
      )}

      {usuario && (
        <FloatingDashboardPedidos
          open={horariosAbierto}
          onClose={() => setHorariosAbierto(false)}
        />
      )}

      {hayUpdateDisponible && (
        <UpdateModal onActualizar={manejarActualizarAhora} />
      )}
    </>
  );
}
